'use client';
import React, { ReactNode, useState } from 'react';

import NextImage, { ImageProps } from 'next/image';
import { twMerge } from 'tailwind-merge';

import { NextImagePlaceholder } from './NextImagePlaceholder';
import { customLoaderProps, NextImageWithLoader } from './NextImageWithLoader';

export interface IImageProps extends ImageProps {
  wrapperClassName?: string;
  imageClassName?: string;
  showPlaceholder?: boolean;
  customPlaceholderClassName?: string;
  customPlaceholderIcon?: ReactNode;
  customLoader?: customLoaderProps | undefined;
}

/**
 * A wrapper component for Next.js's `Image` component that provides additional features:
 * - Optional placeholder display while the image is loading.
 * - Customizable wrapper and image class names for styling.
 * - Support for a custom loader via `NextImageWithLoader`.
 * - Customizable placeholder icon and styling.
 *
 * @component
 * @param {IImageProps} props - The props for the NextImageWrapper component.
 * @param {string} [props.wrapperClassName] - Additional class names for the wrapper div.
 * @param {string} [props.imageClassName] - Additional class names for the image element.
 * @param {boolean} [props.showPlaceholder=true] - Whether to show a placeholder while loading or if no image source is provided.
 * @param {string} [props.customPlaceholderClassName] - Custom class name for the placeholder.
 * @param {ReactNode} [props.customPlaceholderIcon] - Custom icon to display in the placeholder.
 * @param {customLoaderProps} [props.customLoader] - Custom loader configuration for the image.
 * @param {ImageProps} props - All other props are passed to the underlying Next.js Image component.
 *
 * @returns {JSX.Element} The rendered image with optional placeholder and custom loader support.
 */
export const NextImageWrapper = ({
  imageClassName,
  wrapperClassName,
  customPlaceholderClassName,
  customPlaceholderIcon,
  showPlaceholder = true,
  customLoader,
  ...props
}: IImageProps) => {
  const [hasImageLoaded, setHasImageLoaded] = useState(false);
  const { src } = props;

  const renderImage = () => {
    if (!src || src === '') {
      return null;
    }

    const commonProps = {
      className: twMerge(`object-cover`, imageClassName),
      onLoad: (event: React.SyntheticEvent<HTMLImageElement>) => {
        if (props.onLoad) {
          props.onLoad(event);
        }
        setHasImageLoaded(true);
      },
      onError: (event: React.SyntheticEvent<HTMLImageElement>) => {
        if (props.onError) {
          props.onError(event);
        }
        setHasImageLoaded(false);
      },
      ...props,
    };

    if (customLoader) {
      return <NextImageWithLoader {...commonProps} {...customLoader} />;
    }

    return <NextImage {...commonProps} />;
  };

  return (
    <div className={twMerge(`relative`, wrapperClassName)}>
      {showPlaceholder && (!hasImageLoaded || src === '') && (
        <NextImagePlaceholder
          placeholderIcon={customPlaceholderIcon}
          className={customPlaceholderClassName}
        />
      )}
      {renderImage()}
    </div>
  );
};
